import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resetPassword, verifyResetToken } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, ArrowLeft, KeyRound, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import LoginLanguageSwitcher from '../components/auth/LoginLanguageSwitcher';
import { authFlowMessage, authErrorMessage, isTemporaryAuthError, resetLoginIdentifier } from '../lib/authFlow';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const stateToken = typeof location.state?.resetToken === 'string' ? location.state.resetToken : '';
  const token = (searchParams.get('token') || stateToken).trim();
  // A new link gets a new form. No target or password from an old link survives.
  return <ResetPasswordForm key={token} token={token} />;
}

function ResetPasswordForm({ token }) {
  const { t, i18n } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const { user: currentUser, logout } = useAuth();
  const authRef = useRef({ user: currentUser, logout });
  authRef.current = { user: currentUser, logout };
  const translationRef = useRef({ t, i18n });
  translationRef.current = { t, i18n };

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [target, setTarget] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [retryable, setRetryable] = useState(false);
  const [verifyAttempt, setVerifyAttempt] = useState(0);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setVerifying(true);
    setTokenValid(false);
    setTarget(null);
    setError('');
    setRetryable(false);
    if (!token) {
      setVerifying(false);
      setError(translationRef.current.t('auth:reset.missing_token_error'));
      return;
    }
    (async () => {
      try {
        const r = await verifyResetToken(token);
        if (cancelled) return;
        if (r?.data?.valid !== true) {
          setError(authFlowMessage(translationRef.current.i18n, 'invalidReset'));
          return;
        }
        setTokenValid(true);
        setTarget(r.data.target || null);
      } catch (err) {
        if (cancelled) return;
        setRetryable(isTemporaryAuthError(err));
        setError(authErrorMessage(err, translationRef.current.i18n, 'reset'));
      } finally {
        if (!cancelled) setVerifying(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, verifyAttempt]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (inFlight.current || !tokenValid || success || error) return;
    setSubmitError('');

    if (password !== confirmPassword) {
      setSubmitError(t('auth:reset.mismatch_toast'));
      return;
    }

    if (password.length < 6) {
      setSubmitError(t('auth:reset.too_short_toast'));
      return;
    }

    inFlight.current = true;
    setLoading(true);
    try {
      await resetPassword(token, password);
      if (!mounted.current) return;
      setTokenValid(false);
      setPassword('');
      setConfirmPassword('');
      setSuccess(true);
      toast.success(t('auth:reset.success_toast'));
    } catch (err) {
      if (!mounted.current) return;
      if (isTemporaryAuthError(err)) {
        // A lost response may follow a completed password change. Do not
        // claim either success or an expired link without confirmation.
        setSubmitError(authFlowMessage(i18n, 'resetUnconfirmed'));
      } else {
        setTokenValid(false);
        setRetryable(false);
        setError(authErrorMessage(err, i18n, 'reset'));
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) setLoading(false);
    }
  };

  const loginIdentifier = resetLoginIdentifier(target);
  const otherAccount = Boolean(currentUser && target?.user_id && currentUser.id !== target.user_id);
  const handleLogin = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    try {
      // Sign out only after an explicit click. A parent resetting a child's
      // password can keep their own session using the separate continue link.
      if (authRef.current.user) await authRef.current.logout();
      if (!mounted.current) return;
      navigate('/login?mode=password', { state: { email: loginIdentifier } });
    } finally {
      inFlight.current = false;
      if (mounted.current) setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">{t('auth:reset.verifying')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-start justify-center py-8 px-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-1">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-[rgb(var(--tn-text-ffffff))] tracking-tight">
            {t('common:brand.name')}
          </h1>
          {target?.team_name && (
            <p className="text-xs text-muted-foreground tracking-wide" data-testid="brand-team-context">
              {target.team_name}
            </p>
          )}
        </div>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-heading text-2xl">
              {success
                ? t('auth:reset.title_success')
                : error
                  ? t('auth:reset.title_error')
                  : t('auth:reset.title_new')}
            </CardTitle>
            {!success && !error && (
              <CardDescription>
                {t('auth:reset.description_new')}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {success ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto rounded-full bg-green-500/10 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-[rgb(var(--tn-text-22c55e))]" />
                </div>
                <div className="space-y-2">
                  <p className="text-muted-foreground">
                    {t('auth:reset.success_body')}
                  </p>
                  {target?.username && (
                    <p className="text-sm text-muted-foreground">
                      <strong>{target.name}</strong> {t('auth:reset.success_username_body_prefix')}{' '}
                      <code className="bg-muted/30 px-1.5 py-0.5 rounded font-mono text-foreground/90">{target.username}</code>
                    </p>
                  )}
                </div>
                <Button type="button" className="w-full mt-4" disabled={loading} onClick={handleLogin} data-testid="reset-login-btn">
                  {otherAccount ? authFlowMessage(i18n, 'switchAccount') : authFlowMessage(i18n, 'passwordLogin')}
                </Button>
                {otherAccount && <Link to="/" className="block text-sm text-primary hover:underline">{authFlowMessage(i18n, 'keepAccount')}</Link>}
              </div>
            ) : error ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-destructive" />
                </div>
                <div className="space-y-2">
                  <p role="alert" className="text-muted-foreground" data-testid="reset-error">{error}</p>
                </div>
                {retryable && <Button type="button" variant="outline" className="w-full" onClick={() => setVerifyAttempt(value => value + 1)} data-testid="reset-retry-btn">
                  {authFlowMessage(i18n, 'retry')}
                </Button>}
                <Link to="/forgot-password" state={{ email: loginIdentifier }}>
                  <Button variant="outline" className="w-full mt-4">
                    {t('auth:reset.request_new')}
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="ghost" className="w-full">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t('auth:login.back_to_login')}
                  </Button>
                </Link>
              </div>
            ) : tokenValid ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                {target && (
                  <div className="rounded-xl border border-border/60 bg-muted/15 p-4 space-y-1" data-testid="reset-target-block">
                    <p className="text-[10.5px] font-bold uppercase tracking-[0.13em] text-muted-foreground">{t('auth:reset.target_heading')}</p>
                    <p className="text-base font-bold text-[rgb(var(--tn-text-ffffff))] truncate">{target.name}</p>
                    <p className="text-xs text-muted-foreground">{t('auth:reset.target_account_type')} <strong className="text-foreground/85">{target.account_type}</strong></p>
                    {target.username && (
                      <p className="text-xs text-muted-foreground">
                        {t('auth:reset.target_username')} <code className="bg-muted/30 px-1.5 py-0.5 rounded font-mono text-foreground/85">{target.username}</code>
                      </p>
                    )}
                    {!target.username && target.email && (
                      <p className="text-xs text-muted-foreground break-all">
                        {t('auth:reset.target_email')} <span className="text-foreground/85">{target.email}</span>
                      </p>
                    )}
                    {target.team_name && (
                      <p className="text-xs text-muted-foreground">{t('auth:reset.target_team')} <span className="text-foreground/85">{target.team_name}</span></p>
                    )}
                  </div>
                )}
                {currentUser && target?.user_id && currentUser.id !== target.user_id && (
                  <div
                    className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-[rgb(var(--tn-text-fde68a))]"
                    data-testid="reset-logged-in-notice"
                  >
                    <AlertTriangle className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
                    {t('auth:reset.logged_in_notice_prefix')} <strong className="text-[rgb(var(--tn-text-ffffff))]">{currentUser.name || currentUser.email}</strong>{t('auth:reset.logged_in_notice_middle')} <strong className="text-[rgb(var(--tn-text-ffffff))]">{target.name}</strong>.
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="password">{t('auth:reset.password_label')}</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    disabled={loading}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    autoComplete="new-password"
                    data-testid="reset-password-input"
                    className="bg-background border-border"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t('auth:reset.confirm_label')}</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    disabled={loading}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    autoComplete="new-password"
                    data-testid="reset-confirm-input"
                    className="bg-background border-border"
                  />
                </div>
                {submitError && <div className="space-y-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3">
                  <p role="alert" className="text-sm" data-testid="reset-submit-error">{submitError}</p>
                  <Link className="block text-sm text-primary hover:underline" to="/forgot-password" state={{ email: loginIdentifier }}>{authFlowMessage(i18n, 'requestReset')}</Link>
                  <button type="button" disabled={loading} onClick={handleLogin} className="text-sm text-primary hover:underline">
                    {otherAccount ? authFlowMessage(i18n, 'switchAccount') : authFlowMessage(i18n, 'passwordLogin')}
                  </button>
                </div>}
                <Button
                  type="submit"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                  disabled={loading || !password || !confirmPassword}
                  data-testid="reset-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <KeyRound className="w-4 h-4 mr-2" />
                  )}
                  {t('auth:reset.submit')}
                </Button>
              </form>
            ) : null}
          </CardContent>
        </Card>

        <LoginLanguageSwitcher />
      </div>
    </div>
  );
}
